/**
 * Short names for the objects an agent talks about — `w3`, `o1`, `i12` — instead of the
 * document's UUIDs. A name is handed out the first time an object is listed and never
 * reused on that floor of that project, so a model can hold on to it across turns and
 * across undo; every tool accepts either the name or the real id.
 */
import type { Level } from '../core/hp/types';

export type AliasKind = 'wall' | 'opening' | 'item';
const PREFIX: Record<AliasKind, string> = { wall: 'w', opening: 'o', item: 'i' };
const PATTERN = /^([woi])(\d+)$/i;

interface Names { byId: Map<string, string>; byAlias: Map<string, string>; counters: Record<AliasKind, number> }
export interface ScopedAliases {
  alias(kind: AliasKind, id: string): string;
  resolve(kind: AliasKind, reference: string): string;
  sync(level: Level): void;
}
const freshNames = (): Names => ({ byId: new Map(), byAlias: new Map(), counters: { wall: 0, opening: 0, item: 0 } });

export class AliasRegistry {
  private scopes = new Map<string, Names>();
  private current: Names = freshNames();

  /** Names belong to one floor of one project; switching floors switches the book, going back reopens it. */
  enter(scope: string): void {
    let names = this.scopes.get(scope);
    if (!names) { names = freshNames(); this.scopes.set(scope, names); }
    this.current = names;
  }

  /** Hand out names in document order, so the first listing reads w1, w2, w3…. */
  sync(level: Level): void {
    for (const wall of level.walls) this.alias('wall', wall.id);
    for (const opening of level.openings) this.alias('opening', opening.id);
    for (const item of level.items) this.alias('item', item.id);
  }

  alias(kind: AliasKind, id: string): string {
    const known = this.current.byId.get(id);
    if (known) return known;
    const name = PREFIX[kind] + String(++this.current.counters[kind]);
    this.current.byId.set(id, name); this.current.byAlias.set(name, id);
    return name;
  }

  /** The document id behind a name — or the reference itself when it already is an id (or unknown). */
  resolve(kind: AliasKind, reference: string): string {
    const trimmed = reference.trim();
    const match = PATTERN.exec(trimmed);
    if (match && match[1].toLowerCase() === PREFIX[kind]) return this.current.byAlias.get(trimmed.toLowerCase()) ?? trimmed;
    return trimmed;
  }

  /** A lookup bound to another floor's book, for listing a floor that is not the active one. */
  forScope(scope: string, level: Level): (kind: AliasKind, id: string) => string {
    return this.scoped(scope, level).alias;
  }

  scoped(scope: string, level?: Level): ScopedAliases {
    let names = this.scopes.get(scope);
    if (!names) { names = freshNames(); this.scopes.set(scope, names); }
    const book = names;
    const alias = (kind: AliasKind, id: string) => {
      const known = book.byId.get(id);
      if (known) return known;
      const name = PREFIX[kind] + String(++book.counters[kind]);
      book.byId.set(id, name); book.byAlias.set(name, id);
      return name;
    };
    const handle: ScopedAliases = { alias,
      resolve: (kind, reference) => {
        const trimmed = reference.trim(), match = PATTERN.exec(trimmed);
        return match && match[1].toLowerCase() === PREFIX[kind] ? book.byAlias.get(trimmed.toLowerCase()) ?? trimmed : trimmed;
      },
      sync: value => {
        for (const wall of value.walls) alias('wall', wall.id);
        for (const opening of value.openings) alias('opening', opening.id);
        for (const item of value.items) alias('item', item.id);
      },
    };
    if (level) handle.sync(level);
    return handle;
  }

  static looksLikeAlias(reference: string): boolean { return PATTERN.test(reference.trim()); }
}

/** The one book the editor's agents share — the chat, the bridge and the local agent name things the same way. */
export const aliases = new AliasRegistry();

/** The book for a project floor: the project id and the floor index. */
export const aliasScope = (projectId: string | null | undefined, levelIndex: number): string => `${projectId ?? ''}#${levelIndex}`;
